// @ts-expect-error Node strip-types regression checks need an explicit TS extension here.
import { presentSettingsValue } from './settingsPresentation.ts';

export type AdminUiStateSection = 'payment' | 'catalog' | 'orders' | 'safety' | 'drafts';

export const DEFAULT_OVERVIEW_POLL_INTERVAL_MS = 45000;
export const FAST_OVERVIEW_POLL_INTERVAL_MS = 10000;

function normalizeText(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

function presentCatalogRiskCount(count: number) {
  if (count === 1) return '1 ризик каталогу';
  if (count >= 2 && count <= 4) return `${count} ризики каталогу`;
  return `${count} ризиків каталогу`;
}

function fallbackStateValue(value: unknown) {
  const normalized = normalizeText(value);
  return presentSettingsValue(normalized || value);
}

export function presentTopbarEnvironment(overview: any) {
  if (!overview) {
    return { label: 'Перевіряю стан', status: 'pending' };
  }

  const alerts = overview.alerts || [];
  const modeStatus = overview.modeSummary?.status;
  const paymentMode = normalizeText(overview.modeSummary?.payment?.mode || overview.environment?.liqpayMode);

  if (
    overview.status === 'critical'
    || modeStatus === 'critical'
    || modeStatus === 'error'
    || alerts.includes('onec_worker_unreachable')
    || alerts.includes('pay_web_unreachable')
  ) {
    return { label: 'Є збій', status: 'critical' };
  }

  const hasPausedCard = (overview.cards || []).some((card: any) => card.status === 'disabled');
  if (hasPausedCard || modeStatus === 'paused') {
    return { label: 'Пауза в процесах', status: 'paused' };
  }

  if (paymentMode === 'sandbox') {
    return { label: 'Тестові оплати', status: 'warning' };
  }

  if (
    overview.status === 'warning'
    || modeStatus === 'warning'
    || modeStatus === 'degraded'
  ) {
    return { label: 'Є обмеження', status: 'warning' };
  }

  return { label: 'Усе працює', status: 'live' };
}

function hasRunningSyncRow(overview: any) {
  return Object.values(overview?.lastSync || {}).some((row: any) => normalizeText(row?.status) === 'running');
}

export function getOverviewPollIntervalMs(overview: any) {
  if (!overview) {
    return DEFAULT_OVERVIEW_POLL_INTERVAL_MS;
  }

  const status = normalizeText(overview.status);
  const alerts = Array.isArray(overview.alerts) ? overview.alerts : [];
  const hasFailedUpstream = (
    overview?.upstream?.workerHeartbeat?.ok === false ||
    overview?.upstream?.workerSyncStatus?.ok === false ||
    overview?.upstream?.payWebHealth?.ok === false
  );

  if (
    ['critical', 'warning', 'degraded'].includes(status) ||
    alerts.length > 0 ||
    hasFailedUpstream ||
    hasRunningSyncRow(overview)
  ) {
    return FAST_OVERVIEW_POLL_INTERVAL_MS;
  }

  return DEFAULT_OVERVIEW_POLL_INTERVAL_MS;
}

export function presentCurrentStateValue(section: AdminUiStateSection, state: any) {
  const label = normalizeText(state?.label);
  const mode = normalizeText(state?.mode);
  const status = normalizeText(state?.status);

  if (section === 'payment') {
    if (status === 'sandbox' || mode === 'sandbox' || label === 'sandbox') {
      return 'LiqPay приймає тестові оплати';
    }

    if (status === 'live' || status === 'ok' || mode === 'live' || label === 'live') {
      return 'LiqPay приймає реальні оплати';
    }
  }

  if (section === 'catalog') {
    if (label) return fallbackStateValue(label);
    if (status === 'paused') return 'Синхронізація каталогу стоїть на паузі';
    if (status === 'warning' || status === 'degraded') return 'Каталог оновлюється з обмеженнями';
    if (status === 'live' || status === 'ok') return 'Каталог синхронізується у робочому режимі';
  }

  if (section === 'orders') {
    if (status === 'paused') return 'Передача замовлень у 1С вимкнена';
    if (status === 'live' || status === 'ok') return 'Замовлення передаються в 1С автоматично';
    if (label) return fallbackStateValue(label);
  }

  if (section === 'safety') {
    if (status === 'warning') return 'Shopify зараз захищений від бойових записів';
    if (status === 'live' || status === 'ok') return 'Бойовий запис у Shopify дозволений';
    if (label) return fallbackStateValue(label);
  }

  if (section === 'drafts') {
    if (status === 'paused') return 'Автопублікація чернеток зупинена';
    if (status === 'live' || status === 'ok') return 'Чернетки перевіряються на автопублікацію';
    if (label) return fallbackStateValue(label);
  }

  return fallbackStateValue(label || mode || status || state);
}

export function presentCurrentStateBadgeLabel(
  section: AdminUiStateSection,
  state: any,
  options: { issuesCount?: number } = {},
) {
  const statusLabel = normalizeText(state?.statusLabel);
  const status = normalizeText(state?.status);
  const mode = normalizeText(state?.mode);
  const label = normalizeText(state?.label);

  if (statusLabel) {
    return fallbackStateValue(statusLabel);
  }

  if (section === 'catalog' && options.issuesCount) {
    return presentCatalogRiskCount(options.issuesCount);
  }

  if (section === 'payment') {
    if (status === 'sandbox' || mode === 'sandbox' || label === 'sandbox') return 'Тестові оплати';
    if (status === 'live' || status === 'ok' || mode === 'live' || label === 'live') return 'Бойові оплати';
  }

  if (section === 'orders' && status === 'paused') return 'Передача на паузі';
  if (section === 'safety' && status === 'warning') return 'Без запису в Shopify';
  if (section === 'drafts' && status === 'paused') return 'Автопублікація на паузі';
  if (section === 'catalog' && status === 'paused') return 'Синхронізація на паузі';

  if (status === 'warning' || status === 'degraded') return 'Є обмеження';
  if (status === 'paused') return 'На паузі';
  if (status === 'pending') return 'Перевіряється';
  if (status === 'live' || status === 'ok') return 'Працює';

  return undefined;
}
